import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useGyms, type Gym } from "../../api/hooks";

type GymItem = { label: string; basePath: "/plannings" | "/reservation" };

const GYM_ITEMS: GymItem[] = [
  { label: "Plannings", basePath: "/plannings" },
];

const SIMPLE_ITEMS = [
  { to: "/equipe", label: "L'équipe" },
  { to: "/tarifs", label: "Tarifs" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const { data: gyms } = useGyms();
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const defaultSlug = gyms?.[0]?.slug ?? "brest";

  return (
    <header className="sticky top-0 z-40 border-b border-ink-300 bg-paper/95 text-ink-900 backdrop-blur">
      <div className="container mx-auto flex items-center justify-between py-4">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/logo-lion.png"
            alt="Héraclès"
            className="h-10 w-10 object-contain sm:h-11 sm:w-11"
          />
          <span className="font-display text-base font-bold uppercase tracking-wide sm:text-lg">
            Héraclès
          </span>
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          {GYM_ITEMS.map((item) => (
            <GymDropdown key={item.basePath} item={item} gyms={gyms ?? []} />
          ))}
          {SIMPLE_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                clsx(
                  "text-sm font-semibold uppercase tracking-wider transition",
                  isActive ? "text-brand-500" : "text-ink-900 hover:text-brand-500",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          <ReserveDropdown gyms={gyms ?? []} defaultSlug={defaultSlug} />
        </nav>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden"
          aria-label="Menu"
          aria-expanded={open}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {open ? <path d="M6 6l12 12M6 18L18 6" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
          </svg>
        </button>
      </div>

      {open && (
        <nav className="border-t border-ink-300 md:hidden">
          <div className="container flex flex-col gap-1 py-3">
            {GYM_ITEMS.map((item) => (
              <MobileGymGroup key={item.basePath} item={item} gyms={gyms ?? []} />
            ))}
            {SIMPLE_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className="py-2 text-sm font-semibold uppercase tracking-wider"
              >
                {item.label}
              </NavLink>
            ))}
            <MobileTrialGroup gyms={gyms ?? []} />
            <MobileGymGroup
              item={{ label: "Réserver un cours", basePath: "/reservation" }}
              gyms={gyms ?? []}
              accent
            />
          </div>
        </nav>
      )}
    </header>
  );
}

function GymDropdown({ item, gyms }: { item: GymItem; gyms: Gym[] }) {
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<number | null>(null);
  const location = useLocation();
  const isActive = location.pathname.startsWith(item.basePath);

  const onEnter = () => {
    if (closeTimeout.current) window.clearTimeout(closeTimeout.current);
    setOpen(true);
  };
  const onLeave = () => {
    closeTimeout.current = window.setTimeout(() => setOpen(false), 120);
  };

  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={clsx(
          "flex items-center gap-1 text-sm font-semibold uppercase tracking-wider transition",
          isActive ? "text-brand-500" : "text-ink-900 hover:text-brand-500",
        )}
      >
        {item.label}
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
          <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>

      {open && gyms.length > 0 && (
        <div
          role="menu"
          className="absolute left-1/2 top-full -translate-x-1/2 pt-2"
        >
          <ul className="min-w-[220px] overflow-hidden rounded-md border border-ink-300 bg-paper shadow-lg">
            {gyms.map((g) => (
              <li key={g.id}>
                <Link
                  to={`${item.basePath}/${g.slug}`}
                  role="menuitem"
                  className="block px-4 py-2 text-sm font-semibold uppercase tracking-wider text-ink-900 hover:bg-black/5 hover:text-brand-500"
                >
                  {g.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ReserveDropdown({ gyms, defaultSlug }: { gyms: Gym[]; defaultSlug: string }) {
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<number | null>(null);

  const onEnter = () => {
    if (closeTimeout.current) window.clearTimeout(closeTimeout.current);
    setOpen(true);
  };
  const onLeave = () => {
    closeTimeout.current = window.setTimeout(() => setOpen(false), 120);
  };

  if (gyms.length === 0) {
    return (
      <Link to={`/reservation/${defaultSlug}`} className="btn-primary">
        Réserver
      </Link>
    );
  }

  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="btn-primary inline-flex items-center gap-1"
      >
        Réserver
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
          <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>

      {open && (
        <div role="menu" className="absolute right-0 top-full pt-2">
          <div className="min-w-[260px] overflow-hidden rounded-md border border-ink-300 bg-paper shadow-lg">
            <div className="px-4 pt-3 text-[10px] font-bold uppercase tracking-widest text-ink-500">
              Séance d'essai offerte
            </div>
            <ul className="pb-2">
              {gyms.map((g) => (
                <li key={`trial-${g.id}`}>
                  <Link
                    to={`/contact?gym=${g.slug}&type=trial`}
                    role="menuitem"
                    className="block px-4 py-2 text-sm font-semibold uppercase tracking-wider text-brand-500 hover:bg-black/5"
                  >
                    {g.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="border-t border-ink-300 px-4 pt-3 text-[10px] font-bold uppercase tracking-widest text-ink-500">
              Réserver un cours
            </div>
            <ul className="pb-2">
              {gyms.map((g) => (
                <li key={`reservation-${g.id}`}>
                  <Link
                    to={`/reservation/${g.slug}`}
                    role="menuitem"
                    className="block px-4 py-2 text-sm font-semibold uppercase tracking-wider text-ink-900 hover:bg-black/5 hover:text-brand-500"
                  >
                    {g.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function MobileGymGroup({
  item,
  gyms,
  accent,
}: {
  item: GymItem | { label: string; basePath: "/reservation" };
  gyms: Gym[];
  accent?: boolean;
}) {
  return (
    <div className="py-1">
      <div
        className={clsx(
          "py-2 text-xs font-bold uppercase tracking-widest",
          accent ? "text-brand-500" : "text-ink-500",
        )}
      >
        {item.label}
      </div>
      <div className="flex flex-col gap-1 pl-3">
        {gyms.map((g) => (
          <NavLink
            key={g.id}
            to={`${item.basePath}/${g.slug}`}
            className={({ isActive }) =>
              clsx(
                "py-1.5 text-sm font-semibold uppercase tracking-wide",
                isActive ? "text-brand-500" : "text-ink-900",
              )
            }
          >
            {g.name}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

function MobileTrialGroup({ gyms }: { gyms: Gym[] }) {
  if (gyms.length === 0) return null;
  return (
    <div className="py-1">
      <div className="py-2 text-xs font-bold uppercase tracking-widest text-brand-500">
        Séance d'essai offerte
      </div>
      <div className="flex flex-col gap-1 pl-3">
        {gyms.map((g) => (
          <Link
            key={g.id}
            to={`/contact?gym=${g.slug}&type=trial`}
            className="py-1.5 text-sm font-semibold uppercase tracking-wide text-ink-900"
          >
            {g.name}
          </Link>
        ))}
      </div>
    </div>
  );
}
